import { useEffect, useState } from "react";
import { Mail, Clock, Building2, User, MessageSquare, Phone, Briefcase, RefreshCw, Inbox } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

interface Submission {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  company: string | null;
  business_type: string | null;
  message: string;
  ai_response: string | null;
  created_at: string;
}

const LeadIntelligenceTab = () => {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Submission | null>(null);

  const fetchSubmissions = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("contact_submissions")
      .select("*")
      .order("created_at", { ascending: false });
    if (!error && data) setSubmissions(data);
    setLoading(false);
  };

  useEffect(() => { fetchSubmissions(); }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ color: "hsl(0 0% 95%)" }}>
            <Inbox className="inline mr-2 -mt-1" size={22} /> Lead Intelligence
          </h2>
          <p className="text-sm mt-1" style={{ color: "hsl(0 0% 55%)" }}>
            Inbound contact submissions and AI-qualified responses
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchSubmissions}
          className="border" style={{ borderColor: "hsl(217 40% 28%)", color: "hsl(0 0% 80%)", background: "transparent" }}>
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          { label: "Total Submissions", value: submissions.length },
          { label: "This Week", value: submissions.filter((s) => Date.now() - new Date(s.created_at).getTime() < 7 * 86400000).length },
          { label: "With AI Response", value: submissions.filter((s) => s.ai_response).length },
        ].map((k) => (
          <div key={k.label} className="rounded-xl border p-5"
            style={{ background: "hsl(217 70% 14%)", borderColor: "hsl(217 50% 24%)" }}>
            <p className="text-sm" style={{ color: "hsl(217 25% 70%)" }}>{k.label}</p>
            <p className="mt-1 text-2xl font-bold" style={{ color: "hsl(0 0% 95%)" }}>{k.value}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <p className="text-center py-12" style={{ color: "hsl(217 25% 60%)" }}>Loading submissions…</p>
      ) : submissions.length === 0 ? (
        <p className="text-center py-12" style={{ color: "hsl(217 25% 60%)" }}>No submissions yet.</p>
      ) : (
        <div className="space-y-3">
          {submissions.map((s) => (
            <button key={s.id} onClick={() => setSelected(selected?.id === s.id ? null : s)}
              className="w-full text-left rounded-xl border p-5 transition-all"
              style={{
                background: selected?.id === s.id ? "hsl(217 70% 16%)" : "hsl(217 70% 14%)",
                borderColor: selected?.id === s.id ? "hsl(42 55% 55% / 0.5)" : "hsl(217 50% 24%)",
              }}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <User size={14} style={{ color: "hsl(42 55% 60%)" }} />
                  <span className="font-medium" style={{ color: "hsl(0 0% 92%)" }}>{s.name}</span>
                </div>
                <div className="flex items-center gap-1 text-xs" style={{ color: "hsl(217 25% 60%)" }}>
                  <Clock size={12} />
                  {new Date(s.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm mb-2" style={{ color: "hsl(217 20% 70%)" }}>
                <span className="flex items-center gap-1"><Mail size={12} /> {s.email}</span>
                {s.phone && <span className="flex items-center gap-1"><Phone size={12} /> {s.phone}</span>}
                {s.company && <span className="flex items-center gap-1"><Building2 size={12} /> {s.company}</span>}
                {s.business_type && <span className="flex items-center gap-1"><Briefcase size={12} /> {s.business_type}</span>}
              </div>
              <p className="text-sm line-clamp-2" style={{ color: "hsl(217 18% 75%)" }}>{s.message}</p>
              {selected?.id === s.id && (
                <div className="mt-4 space-y-3 border-t pt-4" style={{ borderColor: "hsl(217 40% 24%)" }}>
                  <div>
                    <p className="text-xs font-medium mb-1 flex items-center gap-1" style={{ color: "hsl(217 25% 65%)" }}>
                      <MessageSquare size={12} /> Full Message
                    </p>
                    <p className="text-sm leading-relaxed" style={{ color: "hsl(0 0% 85%)" }}>{s.message}</p>
                  </div>
                  {s.ai_response && (
                    <div>
                      <p className="text-xs font-medium mb-1" style={{ color: "hsl(42 55% 65%)" }}>AI Response</p>
                      <p className="text-sm leading-relaxed rounded-lg p-3" style={{ background: "hsl(42 55% 55% / 0.08)", color: "hsl(0 0% 85%)" }}>
                        {s.ai_response}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeadIntelligenceTab;
